"use client";

/**
 * Sync AI profiles to/from Supabase so they follow the Google account across devices.
 * - On sign-in: hydrate IndexedDB from Supabase (fetch profiles for user, merge into local db).
 * - On save: when signed in, upsert profile to Supabase (via Dexie hooks).
 */

import { createClient } from "@/lib/supabase/client";
import { db } from "@/lib/db";
import { getCachedAuthUserId, getStoredDeviceUserId } from "@/lib/user/identity";
import type { AiProfile } from "@/types/ai";

const TABLE = "app_ai_profiles";

type DbWithAiProfiles = {
  aiProfiles: {
    hook(event: "creating" | "updating", handler: (...args: any[]) => void): void;
  };
};

export async function fetchProfilesForUser(userId: string): Promise<AiProfile[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from(TABLE)
    .select("payload")
    .eq("user_id", userId);

  if (error) {
    console.warn("[syncProfiles] fetch error:", error.message);
    return [];
  }

  const profiles: AiProfile[] = [];
  for (const row of data ?? []) {
    const payload = row?.payload;
    if (payload && typeof payload === "object" && payload.id && payload.ownerId === userId) {
      profiles.push(payload as AiProfile);
    }
  }
  return profiles;
}

export async function upsertProfileToSupabase(profile: AiProfile): Promise<void> {
  const userId = getCachedAuthUserId();
  if (!userId || profile.ownerId !== userId) return;

  const supabase = createClient();
  const { error } = await supabase
    .from(TABLE)
    .upsert(
      {
        id: profile.id,
        user_id: userId,
        payload: profile,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "id" }
    );

  if (error) {
    console.warn("[syncProfiles] upsert error:", error.message);
  }
}

/**
 * Load profiles from Supabase for the signed-in user and merge into IndexedDB.
 * Call after setting the auth user in context (e.g. in UserProvider).
 */
export async function hydrateFromSupabase(userId: string): Promise<void> {
  const profiles = await fetchProfilesForUser(userId);
  for (const profile of profiles) {
    const normalized = { ...profile, ownerId: userId };
    await db.aiProfiles.put(normalized);
  }
}

/**
 * Reassign profiles created while signed out (device UUID) to the Google/Supabase user.
 * Call after setCachedAuthUserId(authId) so Supabase upserts succeed.
 */
export async function migrateLocalAnonymousProfilesToAuthUser(authUserId: string): Promise<void> {
  const deviceId = getStoredDeviceUserId();
  if (!deviceId || deviceId === authUserId) return;

  try {
    const locals = await db.aiProfiles.where("ownerId").equals(deviceId).toArray();
    for (const p of locals) {
      const updated = { ...p, ownerId: authUserId };
      await db.aiProfiles.put(updated);
      await upsertProfileToSupabase(updated);
    }
  } catch (e) {
    console.warn("[syncProfiles] migrate anonymous profiles error:", e);
  }
}

/**
 * Register Dexie hooks so that when a profile is added or updated locally,
 * we also upsert it to Supabase when the user is signed in.
 * Call once on client init (e.g. in Providers).
 */
export function registerSupabaseProfileHooks(dbInstance: DbWithAiProfiles): void {
  if (typeof window === "undefined") return;

  dbInstance.aiProfiles.hook("creating", (_primKey, profile: AiProfile) => {
    if (getCachedAuthUserId() && profile.ownerId === getCachedAuthUserId()) {
      upsertProfileToSupabase(profile).catch(() => {});
    }
  });

  dbInstance.aiProfiles.hook("updating", (modifications, primKey, obj: AiProfile) => {
    if (getCachedAuthUserId() && obj.ownerId === getCachedAuthUserId()) {
      const updated = { ...obj, ...modifications } as AiProfile;
      upsertProfileToSupabase(updated).catch(() => {});
    }
  });
}
